import React, { useState, useMemo, useCallback } from 'react';
import { Card, Input, Button, Space, Typography, Skeleton, Alert, Tag, Divider, message, Modal, Table } from 'antd';
import { SearchOutlined, TrophyOutlined, BarChartOutlined, ClearOutlined, EyeOutlined, CopyOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import { selectKeywordLoading, selectKeywordResult, selectKeywordError } from '../../Modules/Keyword/Presentation/selectors';
import { optimizeKeywords } from '../../Modules/Keyword/Presentation/actions';
import { clearKeywordState } from '../../Modules/Keyword/Presentation/reducers';
import { KeywordOptimizationRequest } from '../../Modules/Keyword/Model/KeywordOptimization';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { useLocation } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderCard = styled(Card)`
  text-align: center;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const InputCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

const ResultCard = styled(Card)`
  border-radius: 8px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
  
  .field-label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    display: block;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 12px;
`;

const KeywordResult = styled.div`
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fafafa;
  
  &:hover {
    background: #f5f5f5;
    border-color: #d9d9d9;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const StatTag = styled(Tag)`
  && {
    font-size: 16px;
    padding: 12px 20px;
    border-radius: 8px;
    font-weight: 600;
  }
`;

const KeywordsModal = styled(Modal)`
  .ant-modal-body {
    max-height: 400px;
    overflow-y: auto;
  }
`;

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const KeywordItem = styled(Tag)`
  && {
    margin: 0;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 14px;
  }
`;

const KeywordOptimizationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const loading = useAppSelector(selectKeywordLoading);
  const result = useAppSelector(selectKeywordResult);
  const error = useAppSelector(selectKeywordError);
  const location = useLocation() as any;
  const prefill = (location?.state?.keywordPrefill ?? {}) as Partial<Pick<KeywordOptimizationRequest, 'mid' | 'main_keyword' | 'product_url'>>;

  const [formData, setFormData] = useState<KeywordOptimizationRequest>({
    product_url: prefill.product_url ?? '',
    product_name: '',
    main_keyword: prefill.main_keyword ?? '',
    mid: prefill.mid ?? ''
  });

  const [isKeywordsModalVisible, setIsKeywordsModalVisible] = useState(false);

  // Check if user is admin
  if (!currentUser || currentUser.grade !== 'ADMIN') {
    return (
      <PageContainer>
        <Alert
          message="접근 권한이 없습니다"
          description="이 페이지는 관리자만 접근할 수 있습니다."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </PageContainer>
    );
  }

  const handleSubmit = useCallback(async () => {
    if (!formData.product_url.trim() || !formData.product_name.trim() || !formData.main_keyword.trim() || !formData.mid.trim()) {
      message.error('모든 필드를 입력해주세요.');
      return;
    }

    try {
      dispatch(clearKeywordState());
      await dispatch(optimizeKeywords(formData)).unwrap();
      message.success('키워드별 순위 조회가 완료되었습니다.');
    } catch (error) {
      console.error('Keyword ranking failed:', error);
    }
  }, [dispatch, formData]);

  const handleClear = useCallback(() => {
    dispatch(clearKeywordState());
    setFormData({
      product_url: '',
      product_name: '',
      main_keyword: '',
      mid: ''
    });
    message.info('입력 내용과 결과가 모두 지워졌습니다.');
  }, [dispatch]);

  const handleInputChange = useCallback((field: keyof KeywordOptimizationRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const showKeywordsModal = useCallback(() => {
    if (result && result.generated_keywords) {
      setIsKeywordsModalVisible(true);
    } else {
      message.warning('먼저 키워드별 순위를 조회해주세요.');
    }
  }, [result]);

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('키워드를 복사했습니다.');
    } catch {
      message.error('복사에 실패했습니다.');
    }
  }, []);

  const renderSkeleton = useCallback(() => (
    <ResultCard>
      <Skeleton active paragraph={{ rows: 6 }} />
    </ResultCard>
  ), []);

  const top3Columns = useMemo(() => [
    {
      title: '순위',
      dataIndex: 'rank',
      key: 'rank',
      width: 100,
      align: 'center' as const,
      render: (v: number) => (
        <Tag color={v === 1 ? 'gold' : v === 2 ? 'blue' : 'green'} style={{ fontSize: 14, padding: '2px 10px' }}>{v}위</Tag>
      ),
    },
    {
      title: '키워드',
      dataIndex: 'keyword',
      key: 'keyword',
    },
    {
      title: '복사',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: { keyword: string }) => (
        <Button
          type="text"
          icon={<CopyOutlined />}
          onClick={() => handleCopy(record.keyword)}
        />
      ),
    },
  ], [handleCopy]);

  const top3DataSource = useMemo(() => (
    result
      ? result.top_3_keywords
          .slice()
          .sort((a, b) => {
            if (a.rank !== b.rank) return a.rank - b.rank;
            const mk = (result.main_keyword || '').trim().toLowerCase();
            const aHas = mk ? a.keyword.toLowerCase().includes(mk) : false;
            const bHas = mk ? b.keyword.toLowerCase().includes(mk) : false;
            if (aHas !== bHas) return aHas ? -1 : 1; // main keyword 포함 우선
            return a.keyword.localeCompare(b.keyword);
          })
          .map((k, idx) => ({ key: `${k.keyword}-${k.rank}-${idx}`, ...k }))
      : []
  ), [result]);

  const renderResults = () => {
    if (!result) return null;

    return (
      <ResultCard title="키워드별 순위 결과">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Product Info */}
          <div>
            <Title level={4} style={{ color: '#333', margin: '0 0 8px 0' }}>상품 정보</Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div style={{ padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                <Text strong>상품명:</Text> {result.product_name}
              </div>
              <div style={{ padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                <Text strong>메인 키워드:</Text> {result.main_keyword}
              </div>
              <div style={{ padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px' }}>
                <Text strong>상품 ID (MID):</Text> {result.mid}
              </div>
            </Space>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Generated Keywords Summary */}
          <div>
            <Title level={4} style={{ color: '#333', margin: '0 0 8px 0' }}>
              <BarChartOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              생성된 키워드
            </Title>
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <Button 
                type="primary" 
                icon={<EyeOutlined />}
                onClick={showKeywordsModal}
                size="middle"
                disabled={!result || !result.generated_keywords || result.generated_keywords.length === 0}
              >
                {result.total_keywords_generated}개 키워드 생성
              </Button>
            </div>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Top 3 Keywords - Compact Table */}
          <div>
            <Title level={4} style={{ color: '#333', margin: '0 0 8px 0' }}>
              <TrophyOutlined style={{ marginRight: 8, color: '#faad14' }} />
              상위 3위 진입 키워드
            </Title>
            {result.top_3_keywords.length > 0 ? (
              <Table
                columns={top3Columns}
                dataSource={top3DataSource}
                size="middle"
                bordered
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                <Text type="secondary" style={{ fontSize: 14 }}>상위 3위에 진입한 키워드가 없습니다.</Text>
              </div>
            )}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Summary */}
          <div>
            <Title level={4} style={{ color: '#333', margin: '0 0 8px 0' }}>
              <BarChartOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              순위별 통계
            </Title>
            <StatsContainer>
              <StatTag color="gold" style={{ fontSize: 16, padding: '12px 16px' }}>
                1위: {result.summary["1위"]}개
              </StatTag>
              <StatTag color="blue" style={{ fontSize: 16, padding: '12px 16px' }}>
                2위: {result.summary["2위"]}개
              </StatTag>
              <StatTag color="green" style={{ fontSize: 16, padding: '12px 16px' }}>
                3위: {result.summary["3위"]}개
              </StatTag>
            </StatsContainer>
          </div>
        </Space>
      </ResultCard>
    );
  };

  const renderError = () => {
    if (!error) return null;

    return (
      <ResultCard>
        <Alert
          message={error.message}
          description={error.details}
          type="error"
          showIcon
          style={{ fontSize: 14 }}
        />
      </ResultCard>
    );
  };

  return (
    <PageContainer>
      {/* Header */}
      <HeaderCard>
        <Title level={2} style={{ margin: 0 }}>키워드별 순위</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          상품명과 메인 키워드를 기반으로 효과적인 키워드 조합을 찾아보세요
        </Text>
      </HeaderCard>

      {/* Input Form */}
      <InputCard title="키워드별 순위 조회">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <FormField>
            <label className="field-label">상품 URL *</label>
            <Input
              size="large"
              placeholder="https://smartstore.naver.com/..."
              value={formData.product_url}
              onChange={(e) => handleInputChange('product_url', e.target.value)}
            />
          </FormField>

          <FormField>
            <label className="field-label">상품명 *</label>
            <TextArea
              size="large"
              rows={4}
              placeholder="상품명을 입력해주세요"
              value={formData.product_name}
              onChange={(e) => handleInputChange('product_name', e.target.value)}
            />
          </FormField>

          <FormField>
            <label className="field-label">메인 키워드 *</label>
            <Input
              size="large"
              placeholder="메인 키워드를 입력해주세요"
              value={formData.main_keyword}
              onChange={(e) => handleInputChange('main_keyword', e.target.value)}
            />
          </FormField>

          <FormField>
            <label className="field-label">상품 ID (MID) *</label>
            <Input
              size="large"
              placeholder="11979737574 (숫자만 입력)"
              value={formData.mid}
              onChange={(e) => handleInputChange('mid', e.target.value)}
            />
          </FormField>

          <ButtonContainer>
            <Button
              type="primary"
              size="large"
              icon={<SearchOutlined />}
              onClick={handleSubmit}
              loading={loading}
              disabled={!formData.product_url.trim() || !formData.product_name.trim() || !formData.main_keyword.trim() || !formData.mid.trim()}
            >
              조회 시작
            </Button>
            <Button
              size="large"
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={loading}
            >
              초기화
            </Button>
          </ButtonContainer>
        </Space>
      </InputCard>

      {/* Results */}
      {loading && renderSkeleton()}
      {error && renderError()}
      {result && renderResults()}

      {/* Keywords Modal */}
      {result && (
        <KeywordsModal
          title="생성된 키워드 목록"
          open={isKeywordsModalVisible}
          onCancel={() => setIsKeywordsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsKeywordsModalVisible(false)}>
              닫기
            </Button>
          ]}
          width={800}
        >
          <div>
            <Text strong>총 {result.total_keywords_generated}개의 키워드가 생성되었습니다.</Text>
            <KeywordList>
              {result.generated_keywords && result.generated_keywords.length > 0 ? (
                result.generated_keywords.map((keyword, index) => (
                  <KeywordItem key={index} color="blue">
                    {keyword}
                  </KeywordItem>
                ))
              ) : (
                <Text type="secondary">생성된 키워드가 없습니다.</Text>
              )}
            </KeywordList>
          </div>
        </KeywordsModal>
      )}
    </PageContainer>
  );
};

export default KeywordOptimizationPage;
